/**
 * OperatorBadge.jsx · v2.7.63+E47 → E48.1 (Live Session Tracker)
 * ─────────────────────────────────────────────────────────────────────
 * Persistent operator identity overlay for the 4-Window Command Console.
 *
 * When the current page URL carries `?console=<map|chat|helpdesk|email>`
 * a bright amber pill is fixed to the top-right of the viewport showing:
 *   • First name + Last name (falls back to `name` or username)
 *   • Employee ID   (falls back to a subtle "NO EMPLOYEE ID" hint so
 *                    dispatch knows to set it via the driver profile
 *                    edit modal)
 *   • Session duration counter (e.g., "3h 12m") — v2.7.63+E48.1
 *   • Pulsing status dot — v2.7.63+E48.1:
 *       · green (pulse)  — heartbeat succeeded in the last 60s
 *       · amber          — heartbeat stale 60-180s (recovering)
 *       · red            — heartbeat failing > 180s (session in trouble)
 *   • Which of the four consoles this window is (MAP / CHAT / HELPDESK
 *     / EMAILS) — helps supervisors identify screen ownership at a
 *     glance from across the room.
 *
 * When NOT in console mode, the component renders nothing (no visual
 * impact on the normal admin dashboard).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BellRing, Shield, UserCircle2 } from "lucide-react";
import { toast } from "sonner";

const CONSOLE_LABELS = {
  map:      { text: "LIVE MAP", tone: "bg-amber-500/25 border-amber-400 text-amber-100" },
  chat:     { text: "CHAT",     tone: "bg-emerald-500/25 border-emerald-400 text-emerald-100" },
  helpdesk: { text: "HELPDESK", tone: "bg-sky-500/25 border-sky-400 text-sky-100" },
  email:    { text: "EMAILS",   tone: "bg-purple-500/25 border-purple-400 text-purple-100" },
};

// v2.7.63+E48.1 · heartbeat cadence + freshness gates. We deliberately
// poll /admin/me at a slow cadence because this is BADGE indicator
// data — the app already handles real 401 fallout via the axios
// interceptor + authSync broadcast. Cheap, low-priority ping.
const HEARTBEAT_INTERVAL_MS   = 30_000;   // 30 s between health probes
const HEARTBEAT_GREEN_WINDOW  = 60_000;   //   ≤ 60 s stale → green (pulse)
const HEARTBEAT_AMBER_WINDOW  = 180_000;  //   ≤ 180 s stale → amber (warn)

function _readSessionStart(operatorId) {
  // Persist per-operator so switching accounts on the same machine
  // doesn't inherit a stale timer. Fall back to now-if-missing so the
  // counter still ticks from first render.
  const key = `rtg_session_start_${operatorId || "anon"}`;
  const stored = Number(localStorage.getItem(key) || 0);
  if (stored > 0 && stored < Date.now()) return stored;
  const now = Date.now();
  localStorage.setItem(key, String(now));
  return now;
}

function _formatDuration(ms) {
  if (!ms || ms < 0) return "0m";
  const totalMin = Math.floor(ms / 60_000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h > 0) return `${h}h ${m.toString().padStart(2, "0")}m`;
  const s = Math.floor((ms % 60_000) / 1_000);
  if (m > 0) return `${m}m ${s.toString().padStart(2, "0")}s`;
  return `${s}s`;
}


export default function OperatorBadge() {
  const [consoleId, setConsoleId] = useState(null);
  const [operator, setOperator]   = useState(null);
  // v2.7.63+E48.1 · heartbeat state
  const [lastPingAt, setLastPingAt] = useState(null);   // epoch ms of last successful /admin/me
  const [now, setNow] = useState(() => Date.now());
  const [sessionStart, setSessionStart] = useState(null);
  // v2.7.63+E48.3 · registry session id (from the ping response) — keys
  // the per-console nudge WS channel.
  const [sessionId, setSessionId] = useState(null);
  const seenNudgesRef = useRef(new Set());
  const audioCtxRef   = useRef(null);

  // v2.7.63+E48.3 · soft two-tone chime (Web Audio — no asset file).
  // Best-effort: if the browser blocks audio (no prior user gesture)
  // the toast still shows.
  const playChime = useCallback(() => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      if (!audioCtxRef.current) audioCtxRef.current = new Ctx();
      const ctx = audioCtxRef.current;
      if (ctx.state === "suspended") ctx.resume().catch(() => {});
      const t0 = ctx.currentTime;
      [[880, 0], [1174.66, 0.18]].forEach(([freq, offset]) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.0001, t0 + offset);
        gain.gain.exponentialRampToValueAtTime(0.16, t0 + offset + 0.03);
        gain.gain.exponentialRampToValueAtTime(0.0001, t0 + offset + 0.7);
        osc.connect(gain).connect(ctx.destination);
        osc.start(t0 + offset);
        osc.stop(t0 + offset + 0.8);
      });
    } catch (_e) { /* audio best-effort */ }
  }, []);

  // v2.7.63+E48.3 · "I'm here" — clears the pending nudge server-side and
  // bumps last_ping_at (the ack itself is proof-of-life → row snaps green).
  const ackNudge = useCallback(async (nudge) => {
    try {
      const adminApi = (await import("@/lib/adminApi")).default;
      await adminApi.post("/admin/console-sessions/nudge/ack", {
        console_id: consoleId,
        nudge_id:   nudge.id,
      });
      setLastPingAt(Date.now());
    } catch (_e) { /* best-effort */ }
  }, [consoleId]);

  // v2.7.63+E48.3 · render a supervisor nudge: chime + sticky toast with
  // an explicit acknowledge button. Deduped by nudge id (WS + heartbeat
  // fallback can both deliver the same nudge).
  const handleNudge = useCallback((nudge) => {
    if (!nudge || !nudge.id || seenNudgesRef.current.has(nudge.id)) return;
    seenNudgesRef.current.add(nudge.id);
    playChime();
    toast.custom((t) => (
      <div
        className="flex items-start gap-3 bg-zinc-950/95 backdrop-blur border border-amber-400/60 rounded-lg px-4 py-3 shadow-2xl w-[360px]"
        data-testid="console-nudge-toast"
      >
        <BellRing className="h-5 w-5 text-amber-300 flex-shrink-0 mt-0.5 animate-pulse" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-extrabold text-amber-200">Supervisor check-in</div>
          <div className="text-xs text-zinc-300 mt-0.5 break-words" data-testid="console-nudge-message">
            {nudge.message}
          </div>
          <div className="text-[10px] text-zinc-500 mt-1">
            from {nudge.from_name || "Supervisor"}
          </div>
        </div>
        <button
          onClick={() => { ackNudge(nudge); toast.dismiss(t); }}
          className="flex-shrink-0 px-2.5 py-1.5 rounded-md bg-amber-500/20 border border-amber-400 text-amber-100 text-xs font-bold hover:bg-amber-500/40 transition-colors"
          data-testid="console-nudge-ack-btn"
        >
          I'm here ✓
        </button>
      </div>
    ), { duration: Infinity });
  }, [ackNudge, playChime]);
  const handleNudgeRef = useRef(handleNudge);
  useEffect(() => { handleNudgeRef.current = handleNudge; }, [handleNudge]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const c = params.get("console");
    if (c && CONSOLE_LABELS[c]) setConsoleId(c);
  }, []);

  // Initial /admin/me + subsequent heartbeat pings.
  useEffect(() => {
    if (!consoleId) return;
    let cancelled = false;
    const ping = async () => {
      try {
        const adminApi = (await import("@/lib/adminApi")).default;
        const { data } = await adminApi.get("/admin/me");
        if (!cancelled) {
          setOperator(data || null);
          setLastPingAt(Date.now());
        }
        // v2.7.63+E48.2 · Register this console window in the fleet-wide
        // Console Session Registry so /admin/console-sessions can surface
        // it to floor supervisors. Fire-and-forget — never blocks the
        // badge UI even if the registry endpoint is down.
        // v2.7.63+E48.3 · the response now carries our session_id (keys
        // the nudge WS channel) + any pending_nudge missed while the WS
        // was down (30s-heartbeat fallback delivery).
        try {
          const { data: reg } = await adminApi.post("/admin/console-sessions/ping", {
            console_id: consoleId,
            url_href:   window.location.href.slice(0, 2048),
            user_agent: (navigator.userAgent || "").slice(0, 512),
          });
          if (!cancelled && reg?.session_id) setSessionId(reg.session_id);
          if (!cancelled && reg?.pending_nudge) handleNudgeRef.current(reg.pending_nudge);
        } catch (_regErr) { /* registry ping best-effort */ }
      } catch (_e) {
        // Do NOT clear lastPingAt on failure — the badge downgrades
        // to amber then red naturally as time passes.
      }
    };
    ping();
    const id = setInterval(ping, HEARTBEAT_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [consoleId]);

  // v2.7.63+E48.3 · Per-console nudge WS channel. Subscribes to
  // `console:{session_id}` on the fleet bus so a supervisor's nudge lands
  // instantly (toast + chime). Auto-reconnects every 8s on unexpected
  // close; the heartbeat-ping fallback covers any gap.
  useEffect(() => {
    if (!consoleId || !sessionId) return undefined;
    let closed = false;
    let ws = null;
    let retryTimer = null;
    const connect = () => {
      const token = localStorage.getItem("rtg_admin_token");
      const base  = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");
      if (!token || !base) return;
      try {
        ws = new WebSocket(
          `${base}/api/ws/console?token=${encodeURIComponent(token)}&session_id=${encodeURIComponent(sessionId)}`
        );
      } catch (_e) { return; }
      ws.onmessage = (msg) => {
        try {
          const parsed = JSON.parse(msg.data);
          if (parsed?.type === "console_nudge" && parsed.nudge) {
            handleNudgeRef.current(parsed.nudge);
          }
        } catch (_e) { /* non-JSON frame */ }
      };
      ws.onclose = (ev) => {
        if (closed || ev.code === 1008) return; // 1008 = auth rejected, don't hammer
        retryTimer = setTimeout(connect, 8000);
      };
    };
    connect();
    return () => {
      closed = true;
      if (retryTimer) clearTimeout(retryTimer);
      try { ws?.close(1000, "console_unmount"); } catch (_e) { /* ignore */ }
    };
  }, [consoleId, sessionId]);

  // v2.7.63+E48.2 · Best-effort unmount cleanup so a closed console tab
  // disappears from the supervisor overview promptly. Uses sendBeacon
  // when available (survives page unload); falls back to adminApi.post.
  useEffect(() => {
    if (!consoleId) return;
    const end = () => {
      try {
        const token = localStorage.getItem("rtg_admin_token");
        const base  = process.env.REACT_APP_BACKEND_URL || "";
        const url   = `${base}/api/admin/console-sessions/end`;
        const body  = JSON.stringify({ console_id: consoleId });
        // sendBeacon can't attach Authorization headers → the backend
        // requires an admin token, so we fall back to a synchronous
        // fetch with keepalive:true when a token is present.
        if (token && "fetch" in window) {
          fetch(url, {
            method:  "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body,
            keepalive: true,
          }).catch(() => {});
        } else if (navigator.sendBeacon) {
          navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
        }
      } catch (_e) { /* best-effort */ }
    };
    window.addEventListener("beforeunload", end);
    window.addEventListener("pagehide", end);
    return () => {
      window.removeEventListener("beforeunload", end);
      window.removeEventListener("pagehide", end);
      // Also fire on React unmount (e.g. navigation away from the
      // console URL to a non-console admin route) so the row clears
      // without waiting for the 10-min auto-prune.
      end();
    };
  }, [consoleId]);

  // Fast-ticking clock for the duration counter (1 s cadence). This is
  // cheap: it only forces the badge subtree to re-render, and only
  // when the console overlay is active.
  useEffect(() => {
    if (!consoleId) return;
    const id = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(id);
  }, [consoleId]);

  // Resolve / persist the session-start timestamp once we know who's
  // logged in. Keyed by operator identity so switching accounts on the
  // same machine restarts the counter cleanly.
  useEffect(() => {
    if (!consoleId || !operator) return;
    const opId = operator?.driver?.id
              || operator?.username
              || operator?.driver?.email
              || "anon";
    setSessionStart(_readSessionStart(opId));
  }, [consoleId, operator]);

  if (!consoleId) return null;
  const label = CONSOLE_LABELS[consoleId];
  // /admin/me returns two shapes:
  //   • For driver-elevated tokens: { username, role, roles, driver: {...} }
  //   • For root env-admin tokens:   { username, role, roles, is_super_root: true }
  // Look for identity fields on the driver sub-object first, then fall
  // back to the top-level values so both shapes render cleanly.
  const src = operator?.driver || operator || {};
  const first = src.first_name || (src.name?.split(" ")[0] || operator?.username || "");
  const last  = src.last_name  || (src.name?.split(" ").slice(1).join(" ") || "");
  const fullName = [first, last].filter(Boolean).join(" ") || operator?.username || "Operator";
  const empId = src.employee_id || operator?.employee_id || null;

  // v2.7.63+E48.1 · heartbeat status derivation
  const stale = lastPingAt ? (now - lastPingAt) : Infinity;
  const heartbeatStatus = stale <= HEARTBEAT_GREEN_WINDOW ? "green"
    : stale <= HEARTBEAT_AMBER_WINDOW ? "amber"
    : "red";
  const heartbeatMeta = {
    green: { dot: "bg-emerald-400 shadow-emerald-400/60 animate-pulse", label: "LIVE",     tone: "text-emerald-300" },
    amber: { dot: "bg-amber-400   shadow-amber-400/60",                  label: "STALE",    tone: "text-amber-300"   },
    red:   { dot: "bg-red-500     shadow-red-500/60",                    label: "OFFLINE",  tone: "text-red-400"     },
  }[heartbeatStatus];

  const sessionMs = sessionStart ? (now - sessionStart) : 0;
  const sessionText = _formatDuration(sessionMs);

  return (
    <div
      className="fixed top-2 right-2 z-[9999] flex items-center gap-2 pointer-events-auto"
      data-testid="operator-badge"
    >
      <div className={`px-2 py-1 rounded border font-extrabold text-[10px] uppercase tracking-widest ${label.tone}`}
             data-testid="operator-badge-console-type">
        {label.text}
      </div>
      <div
        className="flex items-center gap-2 bg-zinc-950/90 backdrop-blur border border-amber-400/40 rounded-lg px-3 py-1.5 shadow-2xl"
        data-testid="operator-badge-card"
      >
        {/* v2.7.63+E48.1 · Heartbeat pulse dot — visible ping indicator */}
        <div className="relative flex items-center justify-center flex-shrink-0" title={`${heartbeatMeta.label} · last ping ${Math.floor(stale / 1000)}s ago`}>
          <span
            className={`inline-block w-2.5 h-2.5 rounded-full ${heartbeatMeta.dot} shadow-[0_0_8px_currentColor]`}
            data-testid="operator-badge-heartbeat-dot"
            data-heartbeat-status={heartbeatStatus}
          />
        </div>
        <UserCircle2 className="h-4 w-4 text-amber-300 flex-shrink-0"/>
        <div className="flex flex-col leading-tight">
          <span className="text-xs font-extrabold text-amber-200 max-w-[180px] truncate" data-testid="operator-badge-name">
            {fullName}
          </span>
          {empId ? (
            <span className="text-[10px] font-mono tracking-wider text-zinc-400" data-testid="operator-badge-empid">
              ID · {empId}
            </span>
          ) : (
            <span className="text-[10px] text-red-400/70 italic tracking-wider" data-testid="operator-badge-empid-missing">
              NO EMPLOYEE ID · set via Driver Edit
            </span>
          )}
          {/* v2.7.63+E48.1 · Session duration counter — ticks every second */}
          <span
            className={`text-[10px] font-mono tracking-wider mt-0.5 ${heartbeatMeta.tone}`}
            data-testid="operator-badge-session-duration"
            title="Time since login on this device"
          >
            <span className="opacity-60">SESSION · </span>{sessionText}
          </span>
        </div>
        <Shield className="h-4 w-4 text-emerald-300 flex-shrink-0" title="Session synced across all 4 windows"/>
      </div>
    </div>
  );
}
